import { formatBusinessDateLong } from './date-utils';
import { formatMoneyCents } from './format-money';

/**
 * The editable copy printed on account statements and invoices — the yellow callout
 * box and the legal paragraph — plus the fixed FDCPA notice under it.
 *
 * Statements and invoices used to carry identical copies of this logic. They share
 * it here so an invoice with no invoice copy (#678) provably prints the statement's.
 *
 * Tokens, filled from the association's active late-fee rule:
 *   `$###`      → flat fee amount ("25.00"), or the percent ("1.50%") if no flat amount
 *   `{{grace}}` → grace days as "fifteen (15)"
 * Invoice-only tokens, filled only in an association's invoice copy:
 *   `{{dueDate}}`       → the invoice's due date, "November 15, 2026"
 *   `{{invoiceDate}}`   → the invoice's issue date, same form
 *   `{{invoiceNumber}}` → the invoice number
 */

/** Same defaults the rule editor presents in `association-late-fees-settings.tsx`. */
export const DEFAULT_LATE_FEE_CENTS = 2500;
export const DEFAULT_GRACE_DAYS = 15;

export const DEFAULT_LEGAL_TEXT = 'All amount(s) due shall be payable in full within {{grace}} days of the first of '
	+ 'each month. Thereafter, a late fee of $### added to the owner\'s account becomes part of the total account '
	+ 'balance due. Accounts that remain with a balance due after 90 days are subject to legal action and any cost of '
	+ 'the same will be assessed to the lot owner\'s account and be considered part of the total balance due to the '
	+ 'association.';

/** Hard-coded — always printed after the legal paragraph, never overridable per association. */
export const FDCPA_TEXT = 'The Federal Debt Collections Act allows you to put in writing within 30 days of the date '
	+ 'of this notice to appeal or dispute amount(s) due.';

const DAY_MS = 86_400_000;

/** The association fields the copy reads. Matches the print payloads' `association`. */
export interface DocumentCopySource {
	statementCalloutText?: string | null;
	statementLegalText?: string | null;
	invoiceCalloutText?: string | null;
	invoiceLegalText?: string | null;
	lateFeeAmountCents?: number;
	lateFeePercentBps?: number;
	lateFeeGraceDays?: number;
}

export interface InvoiceCopyFacts {
	issuedOn: number;
	dueOn?: number;
	invoiceNumber?: string;
}

export interface PrintedCopy {
	calloutText: string;
	legalText: string;
	fdcpaText: string;
}

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
	'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty'];

/**
 * Render a small whole number as English words (10 → "ten") for grace days written
 * as "fifteen (15) days". Beyond 30 it falls back to digits — larger grace periods
 * are unusual and clumsy to spell out.
 */
export const numberToWords = (n: number): string => {
	if (!Number.isFinite(n) || n < 0) return String(n);
	if (n < 20) return ONES[n];
	if (n <= 30) {
		const t = Math.floor(n / 10);
		const o = n % 10;
		return o === 0 ? TENS[t] : `${TENS[t]}-${ONES[o]}`;
	}
	return String(n);
};

const fmtMoney = (c: number): string => formatMoneyCents(c).replace(/^\$/, '');

const graceDays = (src: DocumentCopySource | null | undefined): number => src?.lateFeeGraceDays ?? DEFAULT_GRACE_DAYS;

/** Function replacers throughout: a value is inserted literally, never read as a `$&` pattern. */
const fillLateFeeTokens = (text: string, src: DocumentCopySource | null | undefined): string => {
	const lateFee = src?.lateFeeAmountCents != null
		? fmtMoney(src.lateFeeAmountCents)
		: src?.lateFeePercentBps != null
			? `${(src.lateFeePercentBps / 100).toFixed(2)}%`
			: fmtMoney(DEFAULT_LATE_FEE_CENTS);
	const days = graceDays(src);
	const grace = `${numberToWords(days)} (${days})`;
	return text
		.replace(/\$###/g, () => lateFee)
		.replace(/\{\{grace\}\}/g, () => grace);
};

/**
 * A missing due date is the invoice date plus the grace period — the same window the
 * default legal copy promises — so `{{dueDate}}` never prints raw or as a blank.
 */
const fillInvoiceTokens = (text: string, src: DocumentCopySource | null | undefined, invoice: InvoiceCopyFacts): string => {
	const dueOn = invoice.dueOn ?? invoice.issuedOn + graceDays(src) * DAY_MS;
	return text
		.replace(/\{\{dueDate\}\}/g, () => formatBusinessDateLong(dueOn, ''))
		.replace(/\{\{invoiceDate\}\}/g, () => formatBusinessDateLong(invoice.issuedOn, ''))
		.replace(/\{\{invoiceNumber\}\}/g, () => invoice.invoiceNumber ?? '');
};

/** No cadence in the default: associations bill monthly, quarterly or annually. */
const defaultCalloutText = (orgName: string | undefined): string => (orgName
	? `${orgName} offers an option for making your association fee payment online. For more information, visit our web site at:`
	: 'We offer an option for making your association fee payment online. For more information, visit our web site at:');

/** Blank or whitespace-only copy is unset — it falls through to the next source. */
const nonBlank = (s: string | null | undefined): string | undefined => (s?.trim() ? s : undefined);

/** The copy for an account statement. Never reads the invoice copy. */
export const statementCopy = (src: DocumentCopySource | null | undefined, orgName: string | undefined): PrintedCopy => ({
	calloutText: src?.statementCalloutText ?? defaultCalloutText(orgName),
	legalText: fillLateFeeTokens(src?.statementLegalText ?? DEFAULT_LEGAL_TEXT, src),
	fdcpaText: FDCPA_TEXT,
});

/**
 * The copy for an invoice: the association's invoice copy, else its statement copy,
 * else the default — chosen per field. With no invoice copy saved the result equals
 * `statementCopy`, which is what invoices printed before #678.
 *
 * Every token is filled in the invoice's OWN copy, callout included. Copy the invoice
 * falls back to is the statement's, filled exactly as on the statement — so it prints
 * byte-for-byte what invoices printed before.
 */
export const invoiceCopy = (
	src: DocumentCopySource | null | undefined,
	orgName: string | undefined,
	invoice: InvoiceCopyFacts,
): PrintedCopy => {
	const statement = statementCopy(src, orgName);
	const fill = (text: string): string => fillInvoiceTokens(fillLateFeeTokens(text, src), src, invoice);
	const callout = nonBlank(src?.invoiceCalloutText);
	const legal = nonBlank(src?.invoiceLegalText);
	return {
		calloutText: callout !== undefined ? fill(callout) : statement.calloutText,
		legalText: legal !== undefined ? fill(legal) : statement.legalText,
		fdcpaText: FDCPA_TEXT,
	};
};
